'use client'

import { useState, useEffect, useRef } from 'react'
import { X, Info, AlertTriangle, CheckCircle, AlertCircle } from 'lucide-react'
import { m, AnimatePresence } from 'framer-motion'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { MessageMarkdown, type MessageVariables } from '@/components/ui/message-markdown'
import { trpc } from '@/lib/trpc'
import { useVisitor } from '@/hooks/use-visitor'
import { useSession } from 'next-auth/react'
import { useLocale } from 'next-intl'
import { cn } from '@/lib/utils'
import type { MessageTargetLocation, MessageVariant, SystemMessage as SystemMessageType } from '@/generated/prisma/client'

// Polling interval for checking new messages (1 minute)
const POLL_INTERVAL_MS = 60 * 1000

interface SystemMessageDisplayProps {
  targetLocation: MessageTargetLocation
  artistId?: string
  eventId?: string
  /** Artist handle for resolving [[dashboard/...]] links in message content */
  artistHandle?: string
  /**
   * Variables for interpolation in message content.
   * Available variables: userName, userFirstName, userEmail, artistName, artistHandle
   */
  variables?: MessageVariables
  className?: string
  /** Disable polling for new messages (useful for static pages) */
  disablePolling?: boolean
  /** External trigger to refetch messages (e.g., from WebSocket event) */
  refetchTrigger?: number
}

// Variant styles and icons
const variantConfig: Record<
  MessageVariant,
  { bg: string; border: string; text: string; icon: typeof Info; toastType: 'info' | 'warning' | 'success' | 'error' }
> = {
  info: {
    bg: 'bg-blue-500/10',
    border: 'border-blue-500/30',
    text: 'text-blue-300',
    icon: Info,
    toastType: 'info',
  },
  warning: {
    bg: 'bg-amber-500/10',
    border: 'border-amber-500/30',
    text: 'text-amber-300',
    icon: AlertTriangle,
    toastType: 'warning',
  },
  success: {
    bg: 'bg-emerald-500/10',
    border: 'border-emerald-500/30',
    text: 'text-emerald-300',
    icon: CheckCircle,
    toastType: 'success',
  },
  error: {
    bg: 'bg-red-500/10',
    border: 'border-red-500/30',
    text: 'text-red-300',
    icon: AlertCircle,
    toastType: 'error',
  },
}

// Animation variants for smooth enter/exit
const bannerVariants = {
  initial: {
    opacity: 0,
    height: 0,
    marginBottom: 0,
  },
  animate: {
    opacity: 1,
    height: 'auto',
    marginBottom: 8,
    transition: {
      height: { duration: 0.3, ease: [0.4, 0, 0.2, 1] as const },
      opacity: { duration: 0.2, delay: 0.1 },
      marginBottom: { duration: 0.3, ease: [0.4, 0, 0.2, 1] as const },
    },
  },
  exit: {
    opacity: 0,
    height: 0,
    marginBottom: 0,
    transition: {
      height: { duration: 0.25, ease: [0.4, 0, 1, 1] as const, delay: 0.05 },
      opacity: { duration: 0.15 },
      marginBottom: { duration: 0.25, ease: [0.4, 0, 1, 1] as const },
    },
  },
}

// Single message banner component
function MessageBanner({
  message,
  artistHandle,
  variables,
  onDismiss,
  onSnooze,
}: {
  message: SystemMessageType
  artistHandle?: string
  variables?: MessageVariables
  onDismiss: () => void
  onSnooze?: () => void
}) {
  const config = variantConfig[message.variant]
  const Icon = config.icon
  const hasActions = message.ctaText || message.snoozeEnabled

  return (
    <div
      className={cn(
        'relative rounded-lg border px-4 py-3',
        config.bg,
        config.border
      )}
    >
      <div className="flex items-start gap-3">
        <Icon className={cn('h-5 w-5 mt-0.5 shrink-0', config.text)} />
        <div className="flex-1 min-w-0">
          {message.title && (
            <div className={cn('font-medium text-sm', config.text)}>
              <MessageMarkdown artistHandle={artistHandle} variables={variables}>
                {message.title}
              </MessageMarkdown>
            </div>
          )}
          <div
            className={cn(
              'text-sm',
              message.title ? 'text-zinc-300 mt-0.5' : config.text
            )}
          >
            <MessageMarkdown artistHandle={artistHandle} variables={variables}>
              {message.message}
            </MessageMarkdown>
          </div>
          {/* Action buttons */}
          {hasActions && (
            <div className="flex items-center gap-2 mt-2">
              {message.ctaText && (
                <Button
                  size="sm"
                  onClick={onDismiss}
                  className={cn(
                    'h-7 px-3 text-xs',
                    message.variant === 'error' && 'bg-red-600 hover:bg-red-700',
                    message.variant === 'warning' && 'bg-amber-600 hover:bg-amber-700',
                    message.variant === 'success' && 'bg-emerald-600 hover:bg-emerald-700',
                    message.variant === 'info' && 'bg-blue-600 hover:bg-blue-700'
                  )}
                >
                  {message.ctaText}
                </Button>
              )}
              {message.snoozeEnabled && onSnooze && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={onSnooze}
                  className="h-7 px-3 text-xs border-zinc-600 text-zinc-400 hover:bg-zinc-800"
                >
                  {message.snoozeText || 'Remind me later'}
                </Button>
              )}
            </div>
          )}
        </div>
        {message.isDismissable && (
          <Button
            variant="ghost"
            size="icon"
            className={cn(
              'h-6 w-6 shrink-0 -mr-1 -mt-1',
              'text-zinc-400 hover:text-white hover:bg-zinc-700/50'
            )}
            onClick={onDismiss}
          >
            <X className="h-4 w-4" />
            <span className="sr-only">Dismiss</span>
          </Button>
        )}
      </div>
    </div>
  )
}

// Modal message component
function MessageModal({
  message,
  artistHandle,
  variables,
  onDismiss,
  onSnooze,
}: {
  message: SystemMessageType
  artistHandle?: string
  variables?: MessageVariables
  onDismiss: () => void
  onSnooze?: () => void
}) {
  const config = variantConfig[message.variant]
  const Icon = config.icon
  const [highlightButton, setHighlightButton] = useState(false)
  const buttonRef = useRef<HTMLButtonElement>(null)

  // Handle attempt to close modal by clicking outside
  const handleOpenChange = (open: boolean) => {
    if (!open) {
      // User tried to dismiss by clicking outside - highlight the button instead
      setHighlightButton(true)
      buttonRef.current?.focus()
      setTimeout(() => setHighlightButton(false), 600)
    }
  }

  return (
    <Dialog open={true} onOpenChange={handleOpenChange}>
      <DialogContent 
        className="bg-zinc-900 border-zinc-800 max-w-md"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        showCloseButton={!message.snoozeEnabled}
      >
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className={cn('p-2 rounded-full', config.bg, config.border, 'border')}>
              <Icon className={cn('h-5 w-5', config.text)} />
            </div>
            <DialogTitle asChild>
              <div className="text-white font-semibold">
                {message.title ? (
                  <MessageMarkdown artistHandle={artistHandle} variables={variables}>
                    {message.title}
                  </MessageMarkdown>
                ) : (
                  'System Message'
                )}
              </div>
            </DialogTitle>
          </div>
          <DialogDescription asChild>
            <div className="text-zinc-300 pt-2">
              <MessageMarkdown artistHandle={artistHandle} variables={variables}>
                {message.message}
              </MessageMarkdown>
            </div>
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex-col gap-2 sm:flex-col">
          {/* Always show CTA button for modals */}
          <Button
            ref={buttonRef}
            onClick={onDismiss}
            className={cn(
              'w-full transition-all',
              message.variant === 'error' && 'bg-red-600 hover:bg-red-700',
              message.variant === 'warning' && 'bg-amber-600 hover:bg-amber-700',
              message.variant === 'success' && 'bg-emerald-600 hover:bg-emerald-700',
              message.variant === 'info' && 'bg-blue-600 hover:bg-blue-700',
              highlightButton && 'ring-2 ring-white ring-offset-2 ring-offset-zinc-900 scale-105'
            )}
          >
            {message.ctaText || 'Got it'}
          </Button>
          {/* Snooze button if enabled */}
          {message.snoozeEnabled && onSnooze && (
            <Button
              variant="outline"
              onClick={onSnooze}
              className="w-full border-zinc-700 text-zinc-400 hover:bg-zinc-800"
            >
              {message.snoozeText || 'Remind me later'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// Main component that fetches and displays messages
export function SystemMessageBanner({
  targetLocation,
  artistId,
  eventId,
  artistHandle,
  variables,
  className,
  disablePolling = false,
  refetchTrigger,
}: SystemMessageDisplayProps) {
  const { visitorId } = useVisitor()
  const { data: session } = useSession()
  const locale = useLocale()
  const utils = trpc.useUtils()

  // Track locally dismissed messages (for immediate UI feedback)
  const [locallyDismissed, setLocallyDismissed] = useState<Set<string>>(new Set())

  // Track which notifications have been shown (to avoid duplicate toasts)
  const shownNotifications = useRef<Set<string>>(new Set())

  // Fetch active messages for this location with optional polling
  const { data: messages, refetch } = trpc.systemMessage.getActive.useQuery(
    {
      targetLocation,
      artistId,
      eventId,
      visitorId: visitorId || undefined,
      locale,
    },
    {
      staleTime: 30 * 1000, // 30 seconds
      refetchOnWindowFocus: false,
      refetchInterval: disablePolling ? false : POLL_INTERVAL_MS,
    }
  )

  // Refetch when external trigger changes (e.g., WebSocket event)
  useEffect(() => {
    if (refetchTrigger !== undefined && refetchTrigger > 0) {
      refetch()
    }
  }, [refetchTrigger, refetch])

  // Dismiss mutations
  const dismissMutation = trpc.systemMessage.dismiss.useMutation({
    onSuccess: () => {
      utils.systemMessage.getActive.invalidate()
    },
  })

  const dismissAsVisitorMutation = trpc.systemMessage.dismissAsVisitor.useMutation({
    onSuccess: () => {
      utils.systemMessage.getActive.invalidate()
    },
  })

  // Snooze mutations
  const snoozeMutation = trpc.systemMessage.snooze.useMutation({
    onSuccess: () => {
      utils.systemMessage.getActive.invalidate()
    },
  })

  const snoozeAsVisitorMutation = trpc.systemMessage.snoozeAsVisitor.useMutation({
    onSuccess: () => {
      utils.systemMessage.getActive.invalidate()
    },
  })

  // View tracking mutations (fire-and-forget, errors are ignored)
  const recordViewMutation = trpc.systemMessage.recordView.useMutation()
  const recordViewAsVisitorMutation = trpc.systemMessage.recordViewAsVisitor.useMutation()

  // Track which messages have been recorded as viewed (to avoid duplicate calls)
  const recordedViews = useRef<Set<string>>(new Set())

  // Record views when messages are first displayed
  useEffect(() => {
    if (!messages) return

    messages.forEach((msg) => {
      // Only record view once per message per session
      if (recordedViews.current.has(msg.id)) return
      recordedViews.current.add(msg.id)

      // Record the view (fire-and-forget)
      if (session?.user) {
        recordViewMutation.mutate({ messageId: msg.id })
      } else if (visitorId) {
        recordViewAsVisitorMutation.mutate({ messageId: msg.id, visitorId })
      }
    })
  }, [messages, session?.user, visitorId, recordViewMutation, recordViewAsVisitorMutation])

  // Handle dismissal
  const handleDismiss = async (messageId: string) => {
    // Immediately hide from UI
    setLocallyDismissed((prev) => new Set(prev).add(messageId))

    try {
      // If user is logged in, use the protected dismiss endpoint
      // Otherwise use the visitor endpoint for anonymous users
      if (session?.user) {
        await dismissMutation.mutateAsync({ messageId })
      } else if (visitorId) {
        await dismissAsVisitorMutation.mutateAsync({
          messageId,
          visitorId,
        })
      }
    } catch (error) {
      // If dismissal fails, we still keep it locally dismissed
      // to avoid annoying the user
      console.error('Failed to persist message dismissal:', error)
    }
  }

  // Handle snooze
  const handleSnooze = async (messageId: string, snoozeDuration: number) => {
    // Immediately hide from UI
    setLocallyDismissed((prev) => new Set(prev).add(messageId))

    try {
      if (session?.user) {
        await snoozeMutation.mutateAsync({ messageId, snoozeDuration })
      } else if (visitorId) {
        await snoozeAsVisitorMutation.mutateAsync({
          messageId,
          visitorId,
          snoozeDuration,
        })
      }
    } catch (error) {
      console.error('Failed to persist message snooze:', error)
    }
  }

  // Show toast notifications for notification-type messages
  useEffect(() => {
    if (!messages) return

    const notifications = messages.filter(
      (msg) => 
        msg.displayType === 'notification' && 
        !locallyDismissed.has(msg.id) &&
        !shownNotifications.current.has(msg.id)
    )

    notifications.forEach((msg) => {
      const config = variantConfig[msg.variant]
      
      // Mark as shown to prevent duplicate toasts
      shownNotifications.current.add(msg.id)

      // Use custom toast to support markdown content
      toast.custom(
        (t) => (
          <div
            className={cn(
              'flex items-start gap-3 w-full max-w-md rounded-lg border p-4 shadow-lg',
              'bg-zinc-900',
              config.border
            )}
          >
            <config.icon className={cn('h-5 w-5 mt-0.5 shrink-0', config.text)} />
            <div className="flex-1 min-w-0">
              {msg.title && (
                <div className={cn('font-medium text-sm', config.text)}>
                  <MessageMarkdown artistHandle={artistHandle} variables={variables}>
                    {msg.title}
                  </MessageMarkdown>
                </div>
              )}
              <div
                className={cn(
                  'text-sm',
                  msg.title ? 'text-zinc-300 mt-0.5' : config.text
                )}
              >
                <MessageMarkdown artistHandle={artistHandle} variables={variables}>
                  {msg.message}
                </MessageMarkdown>
              </div>
            </div>
            {msg.isDismissable && (
              <button
                onClick={() => {
                  toast.dismiss(t)
                  handleDismiss(msg.id)
                }}
                className="text-zinc-400 hover:text-white transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        ),
        {
          id: msg.id,
          duration: Infinity, // Never auto-dismiss - user must manually dismiss
        }
      )
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps -- handleDismiss and variables are stable by intent; including them would re-trigger all toast notifications
  }, [messages, locallyDismissed, artistHandle])

  // Filter messages by type and dismissal status
  const visibleMessages = messages?.filter(
    (msg) => !locallyDismissed.has(msg.id)
  ) ?? []

  const bannerMessages = visibleMessages.filter((msg) => msg.displayType === 'banner')
  const modalMessages = visibleMessages.filter((msg) => msg.displayType === 'modal')
  // Notifications are handled via useEffect above, inline messages could be handled separately

  return (
    <>
      {/* Banner messages */}
      <div className={cn('overflow-hidden', className)}>
        <AnimatePresence mode="sync" initial={false}>
          {bannerMessages.map((message) => (
            <m.div
              key={message.id}
              variants={bannerVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              layout
            >
              <MessageBanner
                message={message}
                artistHandle={artistHandle}
                variables={variables}
                onDismiss={() => handleDismiss(message.id)}
                onSnooze={message.snoozeEnabled ? () => handleSnooze(message.id, message.snoozeDuration) : undefined}
              />
            </m.div>
          ))}
        </AnimatePresence>
      </div>

      {/* Modal messages - show one at a time, highest priority first */}
      {modalMessages.length > 0 && (
        <MessageModal
          message={modalMessages[0]}
          artistHandle={artistHandle}
          variables={variables}
          onDismiss={() => handleDismiss(modalMessages[0].id)}
          onSnooze={modalMessages[0].snoozeEnabled ? () => handleSnooze(modalMessages[0].id, modalMessages[0].snoozeDuration) : undefined}
        />
      )}
    </>
  )
}

// Hook for checking if there are active messages (useful for conditional rendering)
export function useHasSystemMessages(
  targetLocation: MessageTargetLocation,
  options?: { artistId?: string; eventId?: string }
) {
  const { visitorId } = useVisitor()

  const { data: messages } = trpc.systemMessage.getActive.useQuery(
    {
      targetLocation,
      artistId: options?.artistId,
      eventId: options?.eventId,
      visitorId: visitorId || undefined,
    },
    {
      staleTime: 30 * 1000,
      refetchOnWindowFocus: false,
    }
  )

  return (messages?.length ?? 0) > 0
}
